import { localDb, OUTBOX_META, SYNC_LOCK } from "@/lib/localDb";
import { uuidv7 } from "@/lib/uuid";
import { SYNC_PROTOCOL, type OutboxOp, type PersistedOp } from "@/lib/mutators/types";

// Pushing the outbox to POST /sync/push, shared by the page's sync round and the service
// worker's background sync. Nothing here may touch the DOM or the in-memory store.

export interface SyncPushResponse {
  lastMutationSeq: number;
  rejected: { id: string; reason: string }[];
}

// Why the server refused an op; it never comes back, so the change is lost unless redone.
// "error": the server failed applying it in a way a resend would repeat.
export type RejectReason = "invalid" | "deleted" | "not_reorderable" | "not_on_board" | "unknown" | "error";

export interface Rejection {
  op: OutboxOp;
  reason: RejectReason;
}

export interface OutboxBatch {
  clientId: string;
  ops: PersistedOp[];
}

export const REQUEST_TIMEOUT_MS = 30_000;
// SyncController::MAX_MUTATIONS_PER_PUSH; the rest goes in the next batch.
export const MAX_MUTATIONS_PER_PUSH = 100;
export const OUTBOX_SYNC_TAG = "outbox";
// Kept apart from OUTBOX_META: it belongs to the session, so a sign-out clears it with the copy.
export const CSRF_META_KEY = "csrfToken";

// The server refused the client id (another user's, or malformed).
export const CLIENT_ID_TAKEN = 409;
// The CSRF token went stale, e.g. after a long pause.
export const CSRF_STALE = 422;
const HANDLED_STATUSES = [401, CLIENT_ID_TAKEN, CSRF_STALE, 426] as const;
export type HandledStatus = (typeof HANDLED_STATUSES)[number];

const isHandled = (status: number): status is HandledStatus => (HANDLED_STATUSES as readonly number[]).includes(status);

export const SIGNED_OUT = "signed-out";

export type ChannelMessage =
  | typeof SIGNED_OUT
  | { type: "outbox-changed"; from: string }
  | { type: "rejected"; from: string; rejections: Rejection[] };

export function syncChannel(): BroadcastChannel | null {
  return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(SYNC_LOCK);
}

export function announce(message: ChannelMessage): void {
  const tabs = syncChannel();
  tabs?.postMessage(message);
  tabs?.close();
}

// A hung request would hold the sync lock for every tab.
export async function fetchInTime<T>(url: string, init: RequestInit, outerSignal: AbortSignal, read: (response: Response) => Promise<T>): Promise<T> {
  const request = new AbortController();
  const timer = setTimeout(() => request.abort(), REQUEST_TIMEOUT_MS);
  const abort = () => request.abort();
  outerSignal.addEventListener("abort", abort);
  try {
    return await read(await fetch(url, { ...init, credentials: "same-origin", signal: request.signal }));
  } finally {
    clearTimeout(timer);
    outerSignal.removeEventListener("abort", abort);
  }
}

export const readOutbox = () => localDb.outbox.orderBy("seq").toArray();

export async function readOutboxUser(): Promise<unknown> {
  return (await localDb.meta.get(OUTBOX_META.userId))?.value;
}

export async function readSentThrough(): Promise<number> {
  return Number((await localDb.meta.get(OUTBOX_META.sentThrough))?.value ?? 0);
}

export async function readAckedThrough(): Promise<number> {
  return Number((await localDb.meta.get(OUTBOX_META.ackedThrough))?.value ?? 0);
}

// Ops up to it stay in the outbox until a pull brings their effects, but no longer wait.
export async function recordAcknowledged(lastMutationSeq: number): Promise<void> {
  await localDb.transaction("rw", localDb.meta, async () => {
    const acked = Math.max(await readAckedThrough(), lastMutationSeq);
    await localDb.meta.put({ key: OUTBOX_META.ackedThrough, value: acked });
  });
}

const writtenBy = (op: OutboxOp) => op.protocol ?? 1;

export async function renewClientId(): Promise<string> {
  const clientId = uuidv7();
  await localDb.meta.put({ key: OUTBOX_META.clientId, value: clientId });
  return clientId;
}

/**
 * The oldest ops of `owner` after seq `after`, numbered as sent before they leave, in the
 * same transaction that reads them. Null when none wait or the outbox is someone else's.
 * The batch ends before an op another protocol wrote: the server skips any seq below one it
 * has applied, so that op waits for a bundle that speaks its protocol and everything after it too.
 */
export async function claimOutbox(owner: string, after = 0): Promise<{ batch: OutboxBatch; more: boolean } | null> {
  if ((await localDb.outbox.count()) === 0) return null;
  return localDb.transaction("rw", [localDb.outbox, localDb.meta], async () => {
    const waiting = (await readOutbox()).filter((op) => op.seq > after);
    if (waiting.length === 0 || (await readOutboxUser()) !== owner) return null;
    const foreign = waiting.findIndex((op) => writtenBy(op) !== SYNC_PROTOCOL);
    const sendable = foreign === -1 ? waiting : waiting.slice(0, foreign);
    if (sendable.length === 0) return null;
    const ops = sendable.slice(0, MAX_MUTATIONS_PER_PUSH);
    let clientId = (await localDb.meta.get(OUTBOX_META.clientId))?.value as string | undefined;
    if (!clientId) clientId = await renewClientId();
    const sentThrough = Math.max(await readSentThrough(), ops[ops.length - 1].seq);
    await localDb.meta.put({ key: OUTBOX_META.sentThrough, value: sentThrough });
    return { batch: { clientId, ops }, more: sendable.length > ops.length };
  });
}

export async function pushBatch(batch: OutboxBatch, csrfToken: string, signal: AbortSignal): Promise<SyncPushResponse | HandledStatus> {
  const body = {
    clientId: batch.clientId,
    protocol: SYNC_PROTOCOL,
    mutations: batch.ops.map(({ id, seq, name, args }) => ({ id, seq, name, args })),
  };
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify(body),
  };
  return fetchInTime("/sync/push", init, signal, async (response) => {
    if (isHandled(response.status)) return response.status;
    if (!response.ok) throw new Error(`sync push failed: ${response.status}`);
    return (await response.json()) as SyncPushResponse;
  });
}

export function rejectionsIn(batch: OutboxBatch, response: SyncPushResponse): Rejection[] {
  const byId = new Map(batch.ops.map((op) => [op.id, op]));
  return response.rejected.flatMap(({ id, reason }) => {
    const op = byId.get(id);
    return op ? [{ op, reason: reason as RejectReason }] : [];
  });
}

export async function dropRejected(rejections: Rejection[]): Promise<void> {
  await localDb.outbox.bulkDelete(rejections.map((r) => r.op.seq as number)).catch(() => {});
}

// The service worker has no document.cookie to read the token from, so the page leaves it here.
export async function rememberCsrfToken(token: string): Promise<void> {
  if (token) await localDb.meta.put({ key: CSRF_META_KEY, value: token });
}
